import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  IconButton,
  Stack
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Apps as AppsIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Link as LinkIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { WalteroButton, WalteroDataTable, WalteroCard, WalteroTypography } from './waltero';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import axios from 'axios';

const ApplicationsTab = () => {
  const [subTab, setSubTab] = useState('manage');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Application management states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [viewingApplicationDetail, setViewingApplicationDetail] = useState(null);
  const [viewDeleted, setViewDeleted] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    applicationName: '',
    applicationCategory: 'environmental',
    description: '',
    version: '1.0.0'
  });
  
  // Organization access states
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizationApplications, setOrganizationApplications] = useState([]);
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [grantAccessDialogOpen, setGrantAccessDialogOpen] = useState(false);
  const [applicationSearchTerm, setApplicationSearchTerm] = useState('');
  const [searchedApplications, setSearchedApplications] = useState([]);
  const [editConnectionDialogOpen, setEditConnectionDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState('');
  const [bulkSensorIds, setBulkSensorIds] = useState('');
  
  // Triggers and Actions states
  const [availableTriggers, setAvailableTriggers] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  const [applicationTriggerActions, setApplicationTriggerActions] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [triggersActionsLoading, setTriggersActionsLoading] = useState(false);
  
  // Note: triggers, actions, and other management states are already declared by UX engineer
  const [organizationSensors, setOrganizationSensors] = useState([]);
  const [sensorSearchTerm, setSensorSearchTerm] = useState('');
  const [sensorsLoading, setSensorsLoading] = useState(false);
  const [assignmentSearchDialogOpen, setAssignmentSearchDialogOpen] = useState(false);
  const [availableApplications, setAvailableApplications] = useState([]);
  const [selectedSensorForAssignment, setSelectedSensorForAssignment] = useState(null);

  // Triggers & Actions states
  const [triggers, setTriggers] = useState([]);
  const [actions, setActions] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [triggersLoading, setTriggersLoading] = useState(false);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [associationsLoading, setAssociationsLoading] = useState(false);
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [associationDialogOpen, setAssociationDialogOpen] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [editingAction, setEditingAction] = useState(null);
  const [editingAssociation, setEditingAssociation] = useState(null);
  const [triggerSearchTerm, setTriggerSearchTerm] = useState('');
  const [actionSearchTerm, setActionSearchTerm] = useState('');
  const [selectedApplicationForAssociation, setSelectedApplicationForAssociation] = useState('');
  const [triggerForm, setTriggerForm] = useState({
    triggerName: '',
    triggerDescription: '',
    requestType: ''
  });
  const [actionForm, setActionForm] = useState({
    actionName: '',
    actionDescription: '',
    stepFunctionArn: ''
  });
  const [associationForm, setAssociationForm] = useState({
    triggerId: '',
    actionId: '',
    priority: 1,
    isActive: true,
    configuration: '{}'
  });

  const handleSubTabChange = (event, newValue) => {
    setSubTab(newValue);
  };

  // Load initial data
  useEffect(() => {
    loadApplications();
  }, []);

  // Load triggers and actions when triggers tab is active
  useEffect(() => {
    if (subTab === 'triggers') {
      loadTriggers();
      loadActions();
    }
  }, [subTab]);

  // Force reload applications when tab becomes active (fixes cache issues)
  useEffect(() => {
    if (subTab === 'manage') {
      loadApplications();
    }
  }, [subTab]);

  // Helper function to get status color following Waltero design
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'deprecated':
        return 'error';
      default:
        return 'default';
    }
  };

  // Helper function to get application category color using Waltero palette
  const getCategoryColor = (category) => {
    const colors = {
      environmental: 'primary',
      utility: 'secondary',
      industrial: 'warning',
      transportation: 'info',
      healthcare: 'success',
      agriculture: 'primary',
      energy: 'warning',
      security: 'error'
    };
    return colors[category] || 'default';
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add cache-busting parameter to prevent stale data (avoid headers that cause CORS issues)
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.MANAGEMENT.LIST}?_t=${Date.now()}`);
      
      const apps = response.data.data || [];
      setApplications(apps);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError('Failed to load applications. Please try again or contact support if the issue persists.');
    } finally {
      setLoading(false);
    }
  };

  const searchOrganizations = async (searchTerm) => {
    try {
      let response;
      if (!searchTerm || searchTerm.length === 0) {
        // Load all organizations when no search term
        response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.LIST}`);
      } else {
        // Use the search endpoint for filtered results
        response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.SEARCH}?q=${encodeURIComponent(searchTerm)}`);
      }
      setOrganizationOptions(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error searching organizations:', error);
      setOrganizationOptions([]);
    }
  };

  const searchApplications = async (searchTerm) => {
    try {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchedApplications([]);
        return;
      }
      
      // Get already granted application IDs
      const grantedAppIds = organizationApplications.map(app => app.applicationId);
      
      // Filter from all applications based on search term, excluding already granted
      const filtered = applications.filter(app => 
        app.status === 'active' && 
        !grantedAppIds.includes(app.applicationId) &&
        (app.applicationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
         app.applicationCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
         app.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      setSearchedApplications(filtered);
    } catch (error) {
      console.error('Error searching applications:', error);
      setSearchedApplications([]);
    }
  };

  const handleCreateApplication = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.MANAGEMENT.CREATE}`, applicationForm);
      
      if (response.data.success) {
        setApplications(prev => [...prev, response.data.data]);
        setCreateDialogOpen(false);
        resetApplicationForm();
        setError(null);
      }
    } catch (err) {
      console.error('Error creating application:', err);
      setError('Failed to create application: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateApplication = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.MANAGEMENT.UPDATE(editingApplication.applicationId)}`,
        applicationForm
      );
      
      if (response.data.success) {
        setApplications(prev => 
          prev.map(app => 
            app.applicationId === editingApplication.applicationId 
              ? response.data.data 
              : app
          )
        );
        setEditingApplication(null);
        resetApplicationForm();
        setError(null);
      }
    } catch (err) {
      console.error('Error updating application:', err);
      setError('Failed to update application: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteApplication = async (application) => {
    if (window.confirm(`Are you sure you want to deprecate "${application.applicationName}"?`)) {
      try {
        await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.MANAGEMENT.DELETE(application.applicationId)}`);
        
        if (viewDeleted) {
          // If viewing deleted items, update the status
          setApplications(prev => 
            prev.map(app => 
              app.applicationId === application.applicationId 
                ? { ...app, status: 'deprecated' }
                : app
            )
          );
        } else {
          // If not viewing deleted items, remove from the list
          setApplications(prev => 
            prev.filter(app => app.applicationId !== application.applicationId)
          );
        }
        
        setError(null);
      } catch (err) {
        console.error('Error deleting application:', err);
        setError('Failed to delete application: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleBulkAssignSensors = async () => {
    try {
      if (!selectedApplication) {
        setError('Please select an application');
        return;
      }

      if (!bulkSensorIds.trim()) {
        setError('Please enter sensor IDs');
        return;
      }

      // Parse sensor IDs from text (comma or newline separated)
      const sensorIds = bulkSensorIds
        .split(/[,\n]/)
        .map(id => id.trim())
        .filter(id => id.length > 0);

      if (sensorIds.length === 0) {
        setError('No valid sensor IDs found');
        return;
      }

      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.SENSORS.BULK_ASSIGN}`, {
        sensorIds,
        applicationId: selectedApplication,
        notes: 'Bulk assignment via Applications management'
      });

      if (response.data.success) {
        setBulkAssignDialogOpen(false);
        setSelectedApplication('');
        setBulkSensorIds('');
        setError(null);
        alert(`✅ Successfully assigned ${response.data.data.assignedCount} sensors to application`);
      }
    } catch (err) {
      console.error('Error bulk assigning sensors:', err);
      setError('Failed to bulk assign sensors: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleBulkUnassignSensors = async () => {
    try {
      if (!bulkSensorIds.trim()) {
        setError('Please enter sensor IDs');
        return;
      }

      // Parse sensor IDs from text (comma or newline separated)  
      const sensorIds = bulkSensorIds
        .split(/[,\n]/)
        .map(id => id.trim())
        .filter(id => id.length > 0);

      if (sensorIds.length === 0) {
        setError('No valid sensor IDs found');
        return;
      }

      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.SENSORS.BULK_UNASSIGN}`, {
        sensorIds
      });

      if (response.data.success) {
        setBulkAssignDialogOpen(false);
        setBulkSensorIds('');
        setError(null);
        alert(`✅ Successfully unassigned ${response.data.data.unassignedCount} sensors (reverted to organization defaults)`);
      }
    } catch (err) {
      console.error('Error bulk unassigning sensors:', err);
      setError('Failed to bulk unassign sensors: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRestoreApplication = async (application) => {
    if (window.confirm(`Are you sure you want to restore "${application.applicationName}"?`)) {
      try {
        const response = await axios.put(
          `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.MANAGEMENT.UPDATE(application.applicationId)}`,
          { status: 'active' }
        );
        
        if (response.data.success) {
          setApplications(prev => 
            prev.map(app => 
              app.applicationId === application.applicationId 
                ? { ...app, status: 'active' }
                : app
            )
          );
          setError(null);
        }
      } catch (err) {
        console.error('Error restoring application:', err);
        setError('Failed to restore application: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const resetApplicationForm = () => {
    setApplicationForm({
      applicationName: '',
      applicationCategory: 'environmental',
      description: '',
      version: '1.0.0'
    });
  };

  const startEditApplication = (application) => {
    setEditingApplication(application);
    setApplicationForm({
      applicationName: application.applicationName,
      applicationCategory: application.applicationCategory,
      description: application.description,
      version: application.version
    });
  };

  const handleApplicationRowClick = async (event, application) => {
    if (event.target.closest('button')) {
      // Don't trigger row click if user clicked a button
      return;
    }
    
    try {
      setTriggersActionsLoading(true);
      
      // Load connected organizations for this application
      const orgResponse = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.MANAGEMENT.ORGANIZATIONS(application.applicationId)}`
      );
      
      // Load available triggers and actions
      await loadTriggersAndActions();
      
      // Load current trigger-action associations for this application
      await loadApplicationTriggerActions(application.applicationId);
      
      setViewingApplicationDetail({
        ...application,
        connectedOrganizations: orgResponse.data?.data?.organizationConnections || []
      });
      startEditApplication(application);
    } catch (err) {
      console.error('Error loading application details:', err);
      setError('Failed to load application details: ' + (err.response?.data?.error || err.message));
    } finally {
      setTriggersActionsLoading(false);
    }
  };

  const loadTriggersAndActions = async () => {
    try {
      setTriggersLoading(true);
      setActionsLoading(true);
      
      // Load triggers and actions using proper API endpoints
      const [triggersResponse, actionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.TRIGGERS.LIST}?_t=${Date.now()}`),
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.ACTIONS.LIST}?_t=${Date.now()}`)
      ]);
      
      const triggersData = triggersResponse.data.data || [];
      const actionsData = actionsResponse.data.data || [];
      console.log('Setting available triggers:', triggersData);
      console.log('Setting available actions:', actionsData);
      setAvailableTriggers(triggersData);
      setAvailableActions(actionsData);
    } catch (err) {
      console.error('Error loading triggers and actions:', err);
      // Set empty arrays if loading fails
      setAvailableTriggers([]);
      setAvailableActions([]);
    } finally {
      setTriggersLoading(false);
      setActionsLoading(false);
    }
  };

  const loadApplicationTriggerActions = async (applicationId) => {
    try {
      // Load application trigger-action associations
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.TRIGGER_ACTIONS.LIST(applicationId)}?_t=${Date.now()}`);
      setApplicationTriggerActions(response.data.data || []);
    } catch (err) {
      console.error('Error loading application trigger-actions:', err);
      setApplicationTriggerActions([]);
    }
  };

  const handleCreateTriggerAction = async () => {
    try {
      if (!selectedTrigger || !selectedAction || !viewingApplicationDetail) {
        setError('Please select both a trigger and an action');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.TRIGGER_ACTIONS.CREATE(viewingApplicationDetail.applicationId)}`,
        {
          triggerId: selectedTrigger,
          actionId: selectedAction,
          priority: applicationTriggerActions.length + 1,
          isActive: true
        }
      );

      if (response.data.success) {
        await loadApplicationTriggerActions(viewingApplicationDetail.applicationId);
        setSelectedTrigger('');
        setSelectedAction('');
        setError(null);
      }
    } catch (err) {
      console.error('Error creating trigger-action association:', err);
      setError('Failed to create trigger-action association: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteTriggerAction = async (associationId) => {
    try {
      await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.TRIGGER_ACTIONS.DELETE(viewingApplicationDetail.applicationId, associationId)}`);
      await loadApplicationTriggerActions(viewingApplicationDetail.applicationId);
      setError(null);
    } catch (err) {
      console.error('Error deleting trigger-action association:', err);
      setError('Failed to delete association: ' + (err.response?.data?.error || err.message));
    }
  };

  // Note: Triggers and Actions management functions are already implemented by UX engineer

  // Load organization applications when organization is selected on access tab
  useEffect(() => {
    if (selectedOrganization && subTab === 'access') {
      loadOrganizationApplications();
    }
  }, [selectedOrganization, subTab]);

  // Load organization sensors when organization is selected on sensors tab
  useEffect(() => {
    if (selectedOrganization && subTab === 'sensors') {
      loadOrganizationSensors();
    }
  }, [selectedOrganization, subTab]);

  const loadOrganizationApplications = async () => {
    try {
      // Add cache-busting parameter to prevent stale data
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.ORGANIZATIONS.LIST(selectedOrganization)}?_t=${Date.now()}`
      );
      setOrganizationApplications(response.data.data || []);
    } catch (err) {
      console.error('Error loading organization applications:', err);
      setError('Failed to load organization applications');
    }
  };

  const handleGrantAccess = async (applicationId, isDefault = false) => {
    try {
      await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.ORGANIZATIONS.GRANT(selectedOrganization, applicationId)}`,
        { isDefault, notes: '' }
      );
      
      // Reload organization applications
      await loadOrganizationApplications();
      setGrantAccessDialogOpen(false);
      setError(null);
    } catch (err) {
      console.error('Error granting access:', err);
      setError('Failed to grant access: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRevokeAccess = async (applicationId) => {
    if (window.confirm('Are you sure you want to revoke access to this application?')) {
      try {
        await axios.delete(
          `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.ORGANIZATIONS.REVOKE(selectedOrganization, applicationId)}`
        );
        
        await loadOrganizationApplications();
        setError(null);
      } catch (err) {
        console.error('Error revoking access:', err);
        setError('Failed to revoke access: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleSetDefault = async (applicationId) => {
    try {
      await axios.put(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.ORGANIZATIONS.SET_DEFAULT(selectedOrganization)}`,
        { applicationId }
      );
      
      await loadOrganizationApplications();
      setError(null);
    } catch (err) {
      console.error('Error setting default application:', err);
      setError('Failed to set default application: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleConnectionRowClick = async (event, connection) => {
    if (event.target.closest('button')) {
      // Don't trigger row click if user clicked a button
      return;
    }
    
    try {
      setSensorsLoading(true);
      setEditingConnection(connection);
      setEditConnectionDialogOpen(true);
      
      // Load organization sensors and their current assignments
      await loadOrganizationSensors();
      
    } catch (err) {
      console.error('Error loading connection details:', err);
      setError('Failed to load connection details');
    } finally {
      setSensorsLoading(false);
    }
  };

  const loadOrganizationSensors = async () => {
    if (!selectedOrganization) {
      setOrganizationSensors([]);
      return;
    }

    try {
      // Get all devices/sensors for the selected organization (with cache-busting)
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.LIST}?organizationId=${selectedOrganization}&_t=${Date.now()}`);
      const sensors = response.data.data || [];

      // For each sensor, get its current effective application and explicit assignments in parallel (with cache-busting)
      const sensorsWithApps = await Promise.all(
        sensors.map(async (sensor) => {
          const requestTimestamp = Date.now();
          const effectivePromise = axios.get(
            `${API_BASE_URL}${API_ENDPOINTS.SENSORS.EFFECTIVE_APPLICATION(sensor.id)}?organizationId=${selectedOrganization}&_t=${requestTimestamp}`
          );
          const assignmentsPromise = axios.get(
            `${API_BASE_URL}${API_ENDPOINTS.SENSORS.APPLICATION(sensor.id)}?_t=${requestTimestamp}`
          );

          const [effectiveResult, assignmentsResult] = await Promise.allSettled([effectivePromise, assignmentsPromise]);

          let currentApplication = null;
          if (effectiveResult.status === 'fulfilled') {
            currentApplication = effectiveResult.value.data?.data || null;
          } else if (effectiveResult.reason?.response?.status !== 404) {
            console.error(`Error loading effective application for sensor ${sensor.id}:`, effectiveResult.reason);
          }

          let explicitAssignments = [];
          if (assignmentsResult.status === 'fulfilled') {
            const payload = assignmentsResult.value.data?.data;
            if (payload) {
              explicitAssignments = Array.isArray(payload.assignments)
                ? payload.assignments
                : payload.applicationId
                  ? [payload]
                  : [];
            }
          } else if (assignmentsResult.reason?.response?.status !== 404) {
            console.error(`Error loading explicit assignments for sensor ${sensor.id}:`, assignmentsResult.reason);
          }

          return {
            ...sensor,
            currentApplication,
            explicitAssignments
          };
        })
      );

      setOrganizationSensors(sensorsWithApps);
      setSelectedSensorForAssignment((prev) => {
        if (!prev) {
          return prev;
        }
        const updated = sensorsWithApps.find((sensor) => sensor.id === prev.id);
        return updated || prev;
      });
    } catch (err) {
      console.error('Error loading organization sensors:', err);
      setOrganizationSensors([]);
    }
  };

  const handleUpdateConnection = async (isDefault, notes) => {
    try {
      await axios.put(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.ORGANIZATIONS.UPDATE(selectedOrganization, editingConnection.applicationId)}`,
        { isDefault, notes }
      );
      
      await loadOrganizationApplications();
      setEditConnectionDialogOpen(false);
      setEditingConnection(null);
      setError(null);
    } catch (err) {
      console.error('Error updating connection:', err);
      setError('Failed to update connection: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAssignSensorToApplication = async (sensorId, applicationId) => {
    try {
      // Use bulk assignment even for single sensors for consistency
      await axios.post(`${API_BASE_URL}${API_ENDPOINTS.SENSORS.BULK_ASSIGN}`, {
        sensorIds: [sensorId],
        applicationId,
        notes: 'Assigned via Applications management'
      });
      
      // Reload sensor data to update current application display
      await loadOrganizationSensors();
      setError(null);
    } catch (err) {
      console.error('Error assigning sensor:', err);
      setError('Failed to assign sensor: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRemoveSensorAssignment = async (sensorId, applicationId = null) => {
    try {
      // Use bulk unassign even for single sensors for consistency  
      await axios.post(`${API_BASE_URL}${API_ENDPOINTS.SENSORS.BULK_UNASSIGN}`, {
        sensorIds: [sensorId],
        ...(applicationId ? { applicationId } : {})
      });
      
      // Reload sensor data to update current application display
      await loadOrganizationSensors();
      setError(null);
    } catch (err) {
      console.error('Error removing sensor assignment:', err);
      setError('Failed to remove sensor assignment: ' + (err.response?.data?.error || err.message));
    }
  };

  const openAssignmentSearch = async (sensor) => {
    setSelectedSensorForAssignment(sensor);
    setAssignmentSearchDialogOpen(true);
    
    try {
      // Load all available applications for the organization
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.ORGANIZATIONS.LIST(selectedOrganization)}`
      );
      setAvailableApplications(response.data.data || []);
    } catch (err) {
      console.error('Error loading available applications:', err);
      setAvailableApplications([]);
    }
  };

  const getFilteredSensors = () => {
    if (!sensorSearchTerm) return organizationSensors;
    
    return organizationSensors.filter(sensor =>
      sensor.name?.toLowerCase().includes(sensorSearchTerm.toLowerCase()) ||
      sensor.serialnumber?.toString().includes(sensorSearchTerm) ||
      sensor.externalmeterid?.toLowerCase().includes(sensorSearchTerm.toLowerCase())
    );
  };

  const getDefaultApplication = () => {
    return organizationApplications.find(app => app.isDefault);
  };

  const getSensorApplicationChips = (sensor) => {
    const defaultApp = getDefaultApplication();
    const explicitAssignments = sensor.explicitAssignments || [];
    const effectiveApplicationId = sensor.currentApplication?.applicationId || null;
    const effectiveAssignmentType = sensor.currentApplication?.assignmentType || null;

    const chips = explicitAssignments.map((assignment) => ({
      applicationId: assignment.applicationId,
      applicationName: assignment.applicationName,
      applicationCategory: assignment.applicationCategory,
      assignedDate: assignment.assignedDate,
      isDefault: false,
      isExplicit: true,
      isEffective: effectiveApplicationId === assignment.applicationId && effectiveAssignmentType === 'explicit',
      assignmentType: assignment.assignmentType || 'explicit'
    }));

    if (chips.length === 0) {
      if (defaultApp) {
        chips.push({
          applicationId: defaultApp.applicationId,
          applicationName: defaultApp.applicationName,
          applicationCategory: defaultApp.applicationCategory,
          isDefault: true,
          isExplicit: false,
          isEffective: effectiveApplicationId === defaultApp.applicationId && effectiveAssignmentType === 'default',
          assignmentType: 'default'
        });
      } else if (sensor.currentApplication) {
        chips.push({
          applicationId: sensor.currentApplication.applicationId,
          applicationName: sensor.currentApplication.applicationName,
          applicationCategory: sensor.currentApplication.applicationCategory,
          isDefault: false,
          isExplicit: false,
          isEffective: true,
          assignmentType: sensor.currentApplication.assignmentType || 'unknown'
        });
      }
    }

    return chips;
  };

  // Triggers & Actions functions
  const loadTriggers = async () => {
    try {
      setTriggersLoading(true);
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.TRIGGERS.LIST}`);
      const triggersData = response.data.data || [];
      setTriggers(triggersData);
      setAvailableTriggers(triggersData); // Also set available triggers for association dialog
    } catch (err) {
      console.error('Error loading triggers:', err);
      setError('Failed to load triggers: ' + (err.response?.data?.error || err.message));
    } finally {
      setTriggersLoading(false);
    }
  };

  const loadActions = async () => {
    try {
      setActionsLoading(true);
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ACTIONS.LIST}`);
      const actionsData = response.data.data || [];
      setActions(actionsData);
      setAvailableActions(actionsData); // Also set available actions for association dialog
    } catch (err) {
      console.error('Error loading actions:', err);
      setError('Failed to load actions: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionsLoading(false);
    }
  };

  const loadAssociations = async (applicationId) => {
    try {
      setAssociationsLoading(true);
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.TRIGGER_ACTIONS.LIST(applicationId)}`);
      setAssociations(response.data.data || []);
    } catch (err) {
      console.error('Error loading associations:', err);
      setError('Failed to load associations: ' + (err.response?.data?.error || err.message));
    } finally {
      setAssociationsLoading(false);
    }
  };

  const handleCreateTrigger = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.TRIGGERS.CREATE}`, triggerForm);
      if (response.data.success) {
        setTriggers(prev => [...prev, response.data.data]);
        setTriggerDialogOpen(false);
        resetTriggerForm();
        setError(null);
      }
    } catch (err) {
      console.error('Error creating trigger:', err);
      setError('Failed to create trigger: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateTrigger = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}${API_ENDPOINTS.TRIGGERS.UPDATE(editingTrigger.triggerId)}`,
        triggerForm
      );
      if (response.data.success) {
        setTriggers(prev => 
          prev.map(trigger => 
            trigger.triggerId === editingTrigger.triggerId ? response.data.data : trigger
          )
        );
        setTriggerDialogOpen(false);
        setEditingTrigger(null);
        resetTriggerForm();
        setError(null);
      }
    } catch (err) {
      console.error('Error updating trigger:', err);
      setError('Failed to update trigger: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteTrigger = async (trigger) => {
    if (window.confirm(`Are you sure you want to delete the trigger "${trigger.triggerName}"?`)) {
      try {
        await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.TRIGGERS.DELETE(trigger.triggerId)}`);
        setTriggers(prev => prev.filter(t => t.triggerId !== trigger.triggerId));
        setError(null);
      } catch (err) {
        console.error('Error deleting trigger:', err);
        setError('Failed to delete trigger: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleToggleTriggerStatus = async (trigger) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}${API_ENDPOINTS.TRIGGERS.TOGGLE_STATUS(trigger.triggerId)}`
      );
      if (response.data.success) {
        setTriggers(prev => 
          prev.map(t => 
            t.triggerId === trigger.triggerId 
              ? { ...t, status: t.status === 'active' ? 'inactive' : 'active' }
              : t
          )
        );
        setError(null);
      }
    } catch (err) {
      console.error('Error toggling trigger status:', err);
      setError('Failed to toggle trigger status: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCreateAction = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.ACTIONS.CREATE}`, actionForm);
      if (response.data.success) {
        setActions(prev => [...prev, response.data.data]);
        setActionDialogOpen(false);
        resetActionForm();
        setError(null);
      }
    } catch (err) {
      console.error('Error creating action:', err);
      setError('Failed to create action: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateAction = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}${API_ENDPOINTS.ACTIONS.UPDATE(editingAction.actionId)}`,
        actionForm
      );
      if (response.data.success) {
        setActions(prev => 
          prev.map(action => 
            action.actionId === editingAction.actionId ? response.data.data : action
          )
        );
        setActionDialogOpen(false);
        setEditingAction(null);
        resetActionForm();
        setError(null);
      }
    } catch (err) {
      console.error('Error updating action:', err);
      setError('Failed to update action: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteAction = async (action) => {
    if (window.confirm(`Are you sure you want to delete the action "${action.actionName}"?`)) {
      try {
        await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.ACTIONS.DELETE(action.actionId)}`);
        setActions(prev => prev.filter(a => a.actionId !== action.actionId));
        setError(null);
      } catch (err) {
        console.error('Error deleting action:', err);
        setError('Failed to delete action: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleToggleActionStatus = async (action) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}${API_ENDPOINTS.ACTIONS.TOGGLE_STATUS(action.actionId)}`
      );
      if (response.data.success) {
        setActions(prev => 
          prev.map(a => 
            a.actionId === action.actionId 
              ? { ...a, status: a.status === 'active' ? 'inactive' : 'active' }
              : a
          )
        );
        setError(null);
      }
    } catch (err) {
      console.error('Error toggling action status:', err);
      setError('Failed to toggle action status: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCreateAssociation = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.TRIGGER_ACTIONS.CREATE(selectedApplicationForAssociation)}`,
        associationForm
      );
      if (response.data.success) {
        setAssociations(prev => [...prev, response.data.data]);
        setAssociationDialogOpen(false);
        resetAssociationForm();
        setError(null);
      }
    } catch (err) {
      console.error('Error creating association:', err);
      setError('Failed to create association: ' + (err.response?.data?.error || err.message));
    }
  };

  const resetTriggerForm = () => {
    setTriggerForm({
      triggerName: '',
      triggerDescription: '',
      requestType: ''
    });
  };

  const resetActionForm = () => {
    setActionForm({
      actionName: '',
      actionDescription: '',
      stepFunctionArn: ''
    });
  };

  const resetAssociationForm = () => {
    setAssociationForm({
      triggerId: '',
      actionId: '',
      priority: 1,
      isActive: true,
      configuration: '{}'
    });
  };

  const startEditTrigger = (trigger) => {
    setEditingTrigger(trigger);
    setTriggerForm({
      triggerName: trigger.triggerName,
      triggerDescription: trigger.triggerDescription,
      requestType: trigger.requestType || ''
    });
  };

  const startEditAction = (action) => {
    setEditingAction(action);
    setActionForm({
      actionName: action.actionName,
      actionDescription: action.actionDescription,
      stepFunctionArn: action.stepFunctionArn
    });
  };

  const getFilteredTriggers = () => {
    if (!triggerSearchTerm) return triggers;
    return triggers.filter(trigger =>
      trigger.triggerName?.toLowerCase().includes(triggerSearchTerm.toLowerCase()) ||
      trigger.requestType?.toLowerCase().includes(triggerSearchTerm.toLowerCase()) ||
      trigger.triggerDescription?.toLowerCase().includes(triggerSearchTerm.toLowerCase())
    );
  };

  const getFilteredActions = () => {
    if (!actionSearchTerm) return actions;
    return actions.filter(action =>
      action.actionName?.toLowerCase().includes(actionSearchTerm.toLowerCase()) ||
      action.actionDescription?.toLowerCase().includes(actionSearchTerm.toLowerCase())
    );
  };

  const getTriggerStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getActionStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      default:
        return 'default';
    }
  };


  const applicationColumns = [
    {
      id: 'applicationName',
      label: 'Application Name',
      render: (value, row) => (
        <Box>
          <WalteroTypography variant="body2" sx={{ fontWeight: 'medium' }}>
            {value || 'Unnamed Application'}
          </WalteroTypography>
          <WalteroTypography variant="caption" color="textSecondary">
            v{row.version || '1.0.0'}
          </WalteroTypography>
        </Box>
      )
    },
    {
      id: 'applicationCategory',
      label: 'Category',
      render: (value) => (
        <Chip 
          label={value} 
          size="small" 
          color={getCategoryColor(value)}
          variant="filled"
          sx={{ 
            textTransform: 'capitalize',
            fontWeight: 'medium'
          }}
        />
      )
    },
    {
      id: 'description',
      label: 'Description',
      render: (value) => (
        <WalteroTypography 
          variant="body2" 
          color="textSecondary"
          sx={{ 
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={value || 'No description provided'}
        >
          {value || 'No description provided'}
        </WalteroTypography>
      )
    },
    {
      id: 'status',
      label: 'Status',
      render: (value) => (
        <Chip 
          label={value}
          size="small" 
          color={getStatusColor(value)}
          variant="filled"
          icon={value === 'active' ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
          sx={{ 
            textTransform: 'capitalize',
            fontWeight: 'medium'
          }}
        />
      )
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <WalteroButton 
            size="small" 
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={(e) => {
              e.stopPropagation();
              startEditApplication(row);
            }}
            disabled={row.status === 'deprecated'}
          >
            EDIT
          </WalteroButton>
          {row.status === 'deprecated' ? (
            <WalteroButton 
              size="small" 
              variant="contained"
              color="success"
              onClick={(e) => {
                e.stopPropagation();
                handleRestoreApplication(row);
              }}
            >
              RESTORE
            </WalteroButton>
          ) : (
            <WalteroButton 
              size="small" 
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteApplication(row);
              }}
            >
              DELETE
            </WalteroButton>
          )}
        </Box>
      )
    }
  ];

  const organizationApplicationColumns = [
    {
      id: 'applicationName',
      label: 'Application',
      render: (value, row) => (
        <Box>
          <WalteroTypography 
            variant="body2" 
            sx={{ 
              fontWeight: 'medium',
              color: row.status === 'deprecated' ? 'text.disabled' : 'text.primary',
              textDecoration: row.status === 'deprecated' ? 'line-through' : 'none'
            }}
          >
            {value || 'Unknown Application'}
          </WalteroTypography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            {row.isDefault && (
              <Chip 
                label="Organization Default" 
                size="small" 
                color="success" 
                variant="filled"
                icon={<CheckCircleIcon />}
                sx={{ fontSize: '0.75rem' }}
              />
            )}
            {row.status === 'deprecated' && (
              <Chip 
                label="DEPRECATED" 
                size="small" 
                color="error" 
                variant="filled"
                sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 'bold',
                  backgroundColor: '#d32f2f',
                  color: 'white'
                }}
              />
            )}
          </Box>
        </Box>
      )
    },
    {
      id: 'applicationCategory',
      label: 'Category',
      render: (value) => (
        <Chip 
          label={value} 
          size="small" 
          color={getCategoryColor(value)}
          variant="filled"
          sx={{ 
            textTransform: 'capitalize',
            fontWeight: 'medium'
          }}
        />
      )
    },
    {
      id: 'grantedDate',
      label: 'Access Granted',
      render: (value) => (
        <WalteroTypography variant="body2" color="textSecondary">
          {new Date(value).toLocaleDateString()}
        </WalteroTypography>
      )
    },
    {
      id: 'sensorCount',
      label: 'Assigned Sensors',
      render: (value) => (
        <Chip
          label={`${value || 0} sensors`}
          size="small"
          variant="outlined"
          color={value > 0 ? 'primary' : 'default'}
        />
      )
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!row.isDefault && (
            <WalteroButton 
              size="small" 
              variant="outlined"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSetDefault(row.applicationId);
              }}
            >
              SET DEFAULT
            </WalteroButton>
          )}
          <WalteroButton 
            size="small" 
            variant="outlined"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleRevokeAccess(row.applicationId);
            }}
            disabled={row.isDefault}
            title={row.isDefault ? "Cannot revoke default application - set another application as default first" : "Revoke organization access to this application"}
          >
            {row.isDefault ? 'DEFAULT' : 'REVOKE'}
          </WalteroButton>
        </Box>
      )
    }
  ];

  // Filter applications based on viewDeleted toggle
  const filteredApplications = applications.filter(app => 
    viewDeleted ? true : app.status === 'active'
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <WalteroTypography variant="h4" gutterBottom sx={{ color: 'primary.main' }}>
          Applications Management
        </WalteroTypography>
        <WalteroTypography variant="body1" color="textSecondary">
          Manage IoT applications and control organization access to applications and sensor assignments.
        </WalteroTypography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Sub Navigation */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={subTab} onChange={handleSubTabChange}>
          <Tab 
            label="Manage Applications" 
            value="manage"
            icon={<AppsIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Organization Access" 
            value="access"
            icon={<BusinessIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Sensors Management" 
            value="sensors"
            icon={<InfoIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Triggers & Actions" 
            value="triggers"
            icon={<AppsIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>
      
      {/* Applications Management Tab */}
      {subTab === 'manage' && (
        <WalteroCard>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WalteroTypography variant="h6" sx={{ color: 'primary.main' }}>Active Applications</WalteroTypography>
              <FormControlLabel
                control={
                  <Switch
                    checked={viewDeleted}
                    onChange={(e) => setViewDeleted(e.target.checked)}
                    size="small"
                  />
                }
                label="Show deprecated applications"
                sx={{ ml: 2 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <WalteroButton 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                CREATE APPLICATION
              </WalteroButton>
              <WalteroButton 
                variant="outlined"
                onClick={() => setBulkAssignDialogOpen(true)}
              >
                BULK ASSIGN SENSORS
              </WalteroButton>
              <Button
                variant="text"
                onClick={() => loadApplications()}
                disabled={loading}
                size="small"
              >
                🔄 REFRESH
              </Button>
            </Box>
          </Box>
          
          {/* Help Text */}
          <Alert severity="info" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 1 }} />
            <Box>
              <WalteroTypography variant="body2" sx={{ fontWeight: 'medium' }}>
                Application Management
              </WalteroTypography>
              <WalteroTypography variant="caption" color="textSecondary">
                Create and manage IoT applications. Click on any row to view details and connected organizations.
              </WalteroTypography>
            </Box>
          </Alert>
          
          <WalteroDataTable
            data={filteredApplications}
            columns={applicationColumns}
            loading={loading}
            onRowClick={handleApplicationRowClick}
            emptyStateMessage={viewDeleted ? "No deprecated applications found" : "No active applications found. Create your first application to get started."}
          />
          
        </WalteroCard>
      )}
      
      {/* Organization Access Tab */}
      {subTab === 'access' && (
        <WalteroCard>
          {/* Help Text for Organization Access */}
          <Alert severity="info" sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}>
            <InfoIcon sx={{ mr: 1, mt: 0.25 }} />
            <Box>
              <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Organization Application Access Management
              </WalteroTypography>
              <WalteroTypography variant="caption" color="textSecondary" component="div">
                • Select an organization to view and manage their application access<br/>
                • Grant new applications or modify existing access permissions<br/>
                • Click on any application row to manage sensor assignments and settings
              </WalteroTypography>
            </Box>
          </Alert>
          
          <Box sx={{ mb: 3 }}>
            <WalteroTypography variant="h6" sx={{ color: 'primary.main', mb: 1 }} gutterBottom>
              Select Organization
            </WalteroTypography>
            <Autocomplete
              size="small"
              sx={{ minWidth: 300, mb: 2 }}
              options={organizationOptions}
              getOptionLabel={(option) => option.name || ''}
              value={organizationOptions.find(org => org.id === selectedOrganization) || null}
              onChange={(event, newValue) => {
                setSelectedOrganization(newValue ? newValue.id : '');
              }}
              onOpen={() => {
                // Load all organizations when dropdown opens
                searchOrganizations('');
              }}
              onInputChange={(event, newInputValue) => {
                setOrganizationSearchTerm(newInputValue);
                if (newInputValue.length >= 2) {
                  searchOrganizations(newInputValue);
                } else if (newInputValue.length === 0) {
                  // Load all organizations when search is cleared
                  searchOrganizations('');
                } else {
                  setOrganizationOptions([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and select organization to manage"
                  placeholder="Type to search organizations..."
                />
              )}
              loading={loading}
            />
            
            {selectedOrganization && (
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <WalteroButton 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => setGrantAccessDialogOpen(true)}
                >
                  GRANT ACCESS
                </WalteroButton>
                <Button
                  variant="text"
                  onClick={() => loadOrganizationApplications()}
                  disabled={loading}
                  size="small"
                >
                  🔄 REFRESH
                </Button>
              </Box>
            )}
          </Box>

          {selectedOrganization && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <WalteroTypography variant="h6">
                  Applications for Selected Organization
                </WalteroTypography>
                <WalteroTypography variant="body2" color="textSecondary">
                  {organizationApplications.length} applications found
                </WalteroTypography>
              </Box>
              
              {/* Warning for deprecated applications */}
              {organizationApplications.some(app => app.status === 'deprecated') && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <WalteroTypography variant="body2">
                    ⚠️ This organization has access to deprecated applications. 
                    Consider removing access or switching to active applications.
                  </WalteroTypography>
                </Alert>
              )}
              
              <WalteroDataTable
                data={organizationApplications}
                columns={organizationApplicationColumns}
                loading={loading}
                onRowClick={handleConnectionRowClick}
                emptyStateMessage="No applications have been granted to this organization yet. Use the 'GRANT ACCESS' button to provide access to applications."
              />
            </Box>
          )}
          
          {!selectedOrganization && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <WalteroTypography variant="h6" color="textSecondary" gutterBottom>
                No Organization Selected
              </WalteroTypography>
              <WalteroTypography variant="body2" color="textSecondary">
                Select an organization from the dropdown above to view and manage their application access.
              </WalteroTypography>
            </Box>
          )}
        </WalteroCard>
      )}

      {/* Sensors Management Tab */}
      {subTab === 'sensors' && (
        <WalteroCard>
          {/* Help Text for Sensors Management */}
          <Alert severity="info" sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}>
            <InfoIcon sx={{ mr: 1, mt: 0.25 }} />
            <Box>
              <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Sensors Application Management
              </WalteroTypography>
              <WalteroTypography variant="caption" color="textSecondary" component="div">
                • View all sensors and their current application assignments<br/>
                • Assign sensors to any available application<br/>
                • Remove assignments to revert sensors to organization defaults
              </WalteroTypography>
            </Box>
          </Alert>

          {/* Organization selector for sensors */}
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 300 }}
              options={organizationOptions}
              getOptionLabel={(option) => option.name || ''}
              value={organizationOptions.find(org => org.id === selectedOrganization) || null}
              onChange={(event, newValue) => {
                setSelectedOrganization(newValue ? newValue.id : '');
              }}
              onOpen={() => {
                // Load all organizations when dropdown opens
                searchOrganizations('');
              }}
              onInputChange={(event, newInputValue) => {
                if (newInputValue.length >= 2) {
                  searchOrganizations(newInputValue);
                } else if (newInputValue.length === 0) {
                  searchOrganizations('');
                } else {
                  setOrganizationOptions([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select organization to manage sensors"
                  placeholder="Type to search organizations..."
                />
              )}
              loading={loading}
            />
          </Box>

          {selectedOrganization ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <WalteroTypography variant="h6">
                  Sensors for Selected Organization
                </WalteroTypography>
                <WalteroTypography variant="body2" color="textSecondary">
                  {organizationSensors.length} sensors found
                </WalteroTypography>
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Search sensors"
                placeholder="Search by name, serial number, or meter ID..."
                value={sensorSearchTerm}
                onChange={(e) => setSensorSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {sensorsLoading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography>Loading sensors...</Typography>
                  </Box>
                ) : (
                  <List>
                    {getFilteredSensors().map((sensor) => {
                      const applicationChips = getSensorApplicationChips(sensor);
                      const explicitCount = sensor.explicitAssignments?.length || 0;

                      return (
                        <ListItem key={sensor.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                          <ListItemText
                            primary={`${sensor.name || sensor.serialnumber || 'Unnamed'}`}
                            secondary={
                              <Box>
                                <Typography variant="caption" component="div">
                                  Serial: {sensor.serialnumber} | Meter: {sensor.externalmeterid || 'N/A'}
                                </Typography>
                                <Box sx={{ mt: 0.75 }}>
                                  {applicationChips.length > 0 ? (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                      {applicationChips.map((chip) => {
                                        const chipColor = chip.isDefault ? 'secondary' : chip.isEffective ? 'primary' : 'default';
                                        const chipVariant = chip.isDefault ? 'outlined' : chip.isEffective ? 'filled' : 'outlined';
                                        const chipTitleParts = [];
                                        if (chip.isExplicit) {
                                          chipTitleParts.push('Explicit assignment');
                                        }
                                        if (chip.isDefault) {
                                          chipTitleParts.push('Organization default');
                                        }
                                        if (chip.isEffective) {
                                          chipTitleParts.push('Effective application');
                                        }
                                        if (chip.assignedDate) {
                                          chipTitleParts.push(`Assigned ${new Date(chip.assignedDate).toLocaleString()}`);
                                        }

                                        return (
                                          <Chip
                                            key={`${sensor.id}-${chip.applicationId}`}
                                            label={chip.applicationName}
                                            size="small"
                                            color={chipColor}
                                            variant={chipVariant}
                                            onDelete={chip.isExplicit ? () => handleRemoveSensorAssignment(sensor.id, chip.applicationId) : undefined}
                                            deleteIcon={chip.isExplicit ? <CloseIcon /> : undefined}
                                            title={chipTitleParts.join(' • ') || chip.applicationName}
                                            sx={{
                                              opacity: chip.isEffective ? 1 : 0.8,
                                              '& .MuiChip-deleteIcon': {
                                                fontSize: '16px'
                                              }
                                            }}
                                          />
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                                      No application assigned (no organization default set)
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <WalteroButton
                                size="small"
                                variant="outlined"
                                onClick={() => openAssignmentSearch(sensor)}
                              >
                                ASSIGN
                              </WalteroButton>
                              {explicitCount > 0 && (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveSensorAssignment(sensor.id)}
                                  title="Remove all explicit assignments (revert to organization default)"
                                >
                                  REMOVE ALL
                                </Button>
                              )}
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                    {getFilteredSensors().length === 0 && (
                      <ListItem>
                        <ListItemText
                          primary="No sensors found"
                          secondary={sensorSearchTerm ? `No sensors match "${sensorSearchTerm}"` : "No sensors in this organization"}
                        />
                      </ListItem>
                    )}
                  </List>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <WalteroTypography variant="h6" color="textSecondary">
                Select an Organization
              </WalteroTypography>
              <WalteroTypography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Choose an organization from the dropdown above to manage individual sensor assignments
              </WalteroTypography>
            </Box>
          )}
        </WalteroCard>
      )}

      {/* Triggers & Actions Tab */}
      {subTab === 'triggers' && (
        <Box>
          {/* Help Text for Triggers & Actions */}
          <Alert severity="info" sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}>
            <InfoIcon sx={{ mr: 1, mt: 0.25 }} />
            <Box>
              <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Triggers & Actions Management
              </WalteroTypography>
              <WalteroTypography variant="caption" color="textSecondary" component="div">
                • Define EventBridge triggers and Step Function actions<br/>
                • Associate triggers and actions with applications<br/>
                • Configure event-driven automation for your IoT platform
              </WalteroTypography>
            </Box>
          </Alert>

          <Grid container spacing={3}>
            {/* Left side - Triggers */}
            <Grid item xs={12} md={6}>
              <WalteroCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <WalteroTypography variant="h6">Event Triggers</WalteroTypography>
                  <WalteroButton 
                    variant="contained" 
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setTriggerDialogOpen(true)}
                  >
                    CREATE TRIGGER
                  </WalteroButton>
                </Box>
                <WalteroTypography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Define EventBridge events that will activate application workflows
                </WalteroTypography>

                <TextField
                  fullWidth
                  size="small"
                  label="Search triggers"
                  placeholder="Search by name, event source, or type..."
                  value={triggerSearchTerm}
                  onChange={(e) => setTriggerSearchTerm(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
                
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {triggersLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography>Loading triggers...</Typography>
                    </Box>
                  ) : getFilteredTriggers().length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <WalteroTypography variant="body2" color="textSecondary">
                        {triggerSearchTerm ? `No triggers match "${triggerSearchTerm}"` : 'No triggers defined yet'}
                      </WalteroTypography>
                    </Box>
                  ) : (
                    <List>
                      {getFilteredTriggers().map((trigger) => (
                        <ListItem key={trigger.triggerId} sx={{ 
                          border: 1, 
                          borderColor: 'divider', 
                          borderRadius: 1, 
                          mb: 1,
                          p: 2
                        }}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <WalteroTypography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {trigger.triggerName}
                                </WalteroTypography>
                                <WalteroTypography variant="caption" color="textSecondary">
                                  {trigger.requestType || 'No event name set'}
                                </WalteroTypography>
                                {trigger.triggerDescription && (
                                  <WalteroTypography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {trigger.triggerDescription}
                                  </WalteroTypography>
                                )}
                              </Box>
                              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={trigger.status || 'active'}
                                  size="small"
                                  color={getTriggerStatusColor(trigger.status || 'active')}
                                  variant="filled"
                                />
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <WalteroButton
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                  startEditTrigger(trigger);
                                  setTriggerDialogOpen(true);
                                }}
                              >
                                EDIT
                              </WalteroButton>
                              <WalteroButton
                                size="small"
                                variant="outlined"
                                color={trigger.status === 'active' ? 'warning' : 'success'}
                                onClick={() => handleToggleTriggerStatus(trigger)}
                              >
                                {trigger.status === 'active' ? 'DEACTIVATE' : 'ACTIVATE'}
                              </WalteroButton>
                              <WalteroButton
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteTrigger(trigger)}
                              >
                                DELETE
                              </WalteroButton>
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </WalteroCard>
            </Grid>

            {/* Right side - Actions */}
            <Grid item xs={12} md={6}>
              <WalteroCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <WalteroTypography variant="h6">Step Function Actions</WalteroTypography>
                  <WalteroButton 
                    variant="contained" 
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setActionDialogOpen(true)}
                  >
                    CREATE ACTION
                  </WalteroButton>
                </Box>
                <WalteroTypography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Define Step Functions that will process triggered events
                </WalteroTypography>

                <TextField
                  fullWidth
                  size="small"
                  label="Search actions"
                  placeholder="Search by name or category..."
                  value={actionSearchTerm}
                  onChange={(e) => setActionSearchTerm(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
                
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {actionsLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography>Loading actions...</Typography>
                    </Box>
                  ) : getFilteredActions().length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <WalteroTypography variant="body2" color="textSecondary">
                        {actionSearchTerm ? `No actions match "${actionSearchTerm}"` : 'No actions defined yet'}
                      </WalteroTypography>
                    </Box>
                  ) : (
                    <List>
                      {getFilteredActions().map((action) => (
                        <ListItem key={action.actionId} sx={{ 
                          border: 1, 
                          borderColor: 'divider', 
                          borderRadius: 1, 
                          mb: 1,
                          p: 2
                        }}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <WalteroTypography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {action.actionName}
                                </WalteroTypography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={action.status || 'active'}
                                    size="small"
                                    color={getActionStatusColor(action.status || 'active')}
                                    variant="filled"
                                  />
                                </Box>
                                {action.actionDescription && (
                                  <WalteroTypography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {action.actionDescription}
                                  </WalteroTypography>
                                )}
                                {action.stepFunctionArn && (
                                  <WalteroTypography variant="caption" color="textSecondary" sx={{ 
                                    display: 'block', 
                                    mt: 0.5,
                                    fontFamily: 'monospace',
                                    fontSize: '0.7rem',
                                    maxWidth: '250px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    ARN: {action.stepFunctionArn}
                                  </WalteroTypography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <WalteroButton
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                  startEditAction(action);
                                  setActionDialogOpen(true);
                                }}
                              >
                                EDIT
                              </WalteroButton>
                              <WalteroButton
                                size="small"
                                variant="outlined"
                                color={action.status === 'active' ? 'warning' : 'success'}
                                onClick={() => handleToggleActionStatus(action)}
                              >
                                {action.status === 'active' ? 'DEACTIVATE' : 'ACTIVATE'}
                              </WalteroButton>
                              <WalteroButton
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteAction(action)}
                              >
                                DELETE
                              </WalteroButton>
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </WalteroCard>
            </Grid>

            {/* Bottom section - Application Associations */}
            <Grid item xs={12}>
              <WalteroCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <WalteroTypography variant="h6">
                    Application Trigger-Action Associations
                  </WalteroTypography>
                  <WalteroButton
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={async () => {
                      console.log('Available triggers before dialog open:', availableTriggers);
                      console.log('Available actions before dialog open:', availableActions);
                      setAssociationDialogOpen(true);
                      await loadTriggersAndActions();
                    }}
                    disabled={triggersLoading || actionsLoading || availableTriggers.length === 0 || availableActions.length === 0}
                  >
                    CREATE ASSOCIATION
                  </WalteroButton>
                </Box>
                <WalteroTypography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Configure which triggers and actions are active for each application
                </WalteroTypography>

                {/* Application selector */}
                <FormControl fullWidth sx={{ mb: 3, maxWidth: 400 }}>
                  <InputLabel>Select Application</InputLabel>
                  <Select
                    value={selectedApplicationForAssociation}
                    onChange={(e) => {
                      setSelectedApplicationForAssociation(e.target.value);
                      if (e.target.value) {
                        loadAssociations(e.target.value);
                      }
                    }}
                    label="Select Application"
                  >
                    {applications.filter(app => app.status === 'active').map(app => (
                      <MenuItem key={app.applicationId} value={app.applicationId}>
                        {app.applicationName} ({app.applicationCategory})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {selectedApplicationForAssociation ? (
                  <Box>
                    <WalteroTypography variant="h6" sx={{ mb: 2 }}>
                      Associations for {applications.find(app => app.applicationId === selectedApplicationForAssociation)?.applicationName}
                    </WalteroTypography>
                    
                    {associationsLoading ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography>Loading associations...</Typography>
                      </Box>
                    ) : associations.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <WalteroTypography variant="body2" color="textSecondary">
                          No trigger-action associations configured for this application yet
                        </WalteroTypography>
                      </Box>
                    ) : (
                      <List>
                        {associations.map((association) => {
                          const trigger = triggers.find(t => t.triggerId === association.triggerId);
                          const action = actions.find(a => a.actionId === association.actionId);
                          
                          return (
                            <ListItem key={association.associationId} sx={{ 
                              border: 1, 
                              borderColor: 'divider', 
                              borderRadius: 1, 
                              mb: 1,
                              p: 2,
                              backgroundColor: association.isActive ? 'background.paper' : 'action.hover'
                            }}>
                              <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                      Priority {association.priority}: {trigger?.triggerName || 'Unknown Trigger'} → {action?.actionName || 'Unknown Action'}
                                    </WalteroTypography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                      <Chip
                                        label={`Trigger: ${trigger?.triggerName || 'Unknown'}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={`Action: ${action?.actionName || 'Unknown'}`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={association.isActive ? 'Active' : 'Inactive'}
                                        size="small"
                                        color={association.isActive ? 'success' : 'warning'}
                                        variant="filled"
                                      />
                                    </Box>
                                    {association.configuration && association.configuration !== '{}' && (
                                      <WalteroTypography variant="caption" color="textSecondary">
                                        Custom Configuration: {JSON.stringify(JSON.parse(association.configuration), null, 1)}
                                      </WalteroTypography>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <WalteroButton
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                  >
                                    EDIT
                                  </WalteroButton>
                                  <WalteroButton
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                  >
                                    REMOVE
                                  </WalteroButton>
                                </Box>
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <WalteroTypography variant="h6" color="textSecondary">
                      Select an Application
                    </WalteroTypography>
                    <WalteroTypography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Choose an application above to view and manage its trigger-action associations
                    </WalteroTypography>
                  </Box>
                )}
              </WalteroCard>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Create/Edit Application Dialog */}
      <Dialog 
        open={createDialogOpen || editingApplication !== null} 
        onClose={() => {
          setCreateDialogOpen(false);
          setEditingApplication(null);
          setViewingApplicationDetail(null);
          resetApplicationForm();
        }}
        maxWidth={viewingApplicationDetail ? "md" : "sm"}
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingApplication ? <EditIcon /> : <AddIcon />}
            <span>
              {editingApplication ? 
                (viewingApplicationDetail ? 'Application Details' : 'Edit Application') : 
                'Create New Application'
              }
            </span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Application Name"
              value={applicationForm.applicationName}
              onChange={(e) => setApplicationForm(prev => ({
                ...prev,
                applicationName: e.target.value
              }))}
              required
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={applicationForm.applicationCategory}
                onChange={(e) => setApplicationForm(prev => ({
                  ...prev,
                  applicationCategory: e.target.value
                }))}
                label="Category"
              >
                <MenuItem value="environmental">Environmental</MenuItem>
                <MenuItem value="utility">Utility</MenuItem>
                <MenuItem value="industrial">Industrial</MenuItem>
                <MenuItem value="transportation">Transportation</MenuItem>
                <MenuItem value="healthcare">Healthcare</MenuItem>
                <MenuItem value="agriculture">Agriculture</MenuItem>
                <MenuItem value="energy">Energy</MenuItem>
                <MenuItem value="security">Security</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Description"
              value={applicationForm.description}
              onChange={(e) => setApplicationForm(prev => ({
                ...prev,
                description: e.target.value
              }))}
              multiline
              rows={3}
              fullWidth
            />
            
            <TextField
              label="Version"
              value={applicationForm.version}
              onChange={(e) => setApplicationForm(prev => ({
                ...prev,
                version: e.target.value
              }))}
              fullWidth
            />
            
            {/* Connected Organizations Section */}
            {viewingApplicationDetail && viewingApplicationDetail.connectedOrganizations && (
              <Box sx={{ mt: 3 }}>
                <WalteroTypography variant="h6" gutterBottom>
                  Connected Organizations ({viewingApplicationDetail.connectedOrganizations.length})
                </WalteroTypography>
                {viewingApplicationDetail.connectedOrganizations.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No organizations have access to this application yet.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {viewingApplicationDetail.connectedOrganizations.map((connection, index) => (
                      <Grid item xs={12} sm={6} key={`${connection.organizationId}-${index}`}>
                        <Card variant="outlined" sx={{ position: 'relative' }}>
                          <CardContent>
                            <Typography variant="subtitle2">
                              Organization ID: {connection.organizationId}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Granted: {new Date(connection.grantedDate).toLocaleDateString()}
                            </Typography>
                            {connection.isDefault && (
                              <Chip 
                                label="Default Application" 
                                size="small" 
                                color="success" 
                                sx={{ mt: 1 }}
                              />
                            )}
                            {connection.notes && (
                              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                {connection.notes}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Triggers and Actions Section */}
            {viewingApplicationDetail && (
              <Box sx={{ mt: 3 }}>
                <WalteroTypography variant="h6" gutterBottom>
                  Event Automation ({applicationTriggerActions.length} active)
                </WalteroTypography>
                
                {/* Add new trigger-action association */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <WalteroTypography variant="subtitle2" gutterBottom>
                    Add Event Automation
                  </WalteroTypography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Trigger</InputLabel>
                        <Select
                          value={selectedTrigger}
                          onChange={(e) => setSelectedTrigger(e.target.value)}
                          label="Select Trigger"
                        >
                          {availableTriggers.filter(t => t.isActive).map(trigger => (
                            <MenuItem key={trigger.triggerId} value={trigger.triggerId}>
                              {trigger.triggerName} ({trigger.requestType || 'No event name'})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Action</InputLabel>
                        <Select
                          value={selectedAction}
                          onChange={(e) => setSelectedAction(e.target.value)}
                          label="Select Action"
                        >
                          {availableActions.filter(a => a.isActive).map(action => (
                            <MenuItem key={action.actionId} value={action.actionId}>
                              {action.actionName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <WalteroButton
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={handleCreateTriggerAction}
                        disabled={!selectedTrigger || !selectedAction || triggersActionsLoading}
                      >
                        ADD AUTOMATION
                      </WalteroButton>
                    </Grid>
                  </Grid>
                </Box>

                {/* Current trigger-action associations */}
                {triggersActionsLoading ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography>Loading automation settings...</Typography>
                  </Box>
                ) : applicationTriggerActions.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No event automation configured for this application yet. Add triggers and actions above.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {applicationTriggerActions.map((association, index) => (
                      <Grid item xs={12} key={`${association.triggerId}-${association.actionId}`}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Chip 
                                    label={`Priority ${association.priority}`} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                  />
                                  <Chip 
                                    label={association.isActive ? 'Active' : 'Inactive'}
                                    size="small"
                                    color={association.isActive ? 'success' : 'default'}
                                  />
                                </Box>
                                
                                <WalteroTypography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  <strong>Trigger:</strong> {association.trigger?.triggerName || 'Unknown Trigger'}
                                </WalteroTypography>
                                <WalteroTypography variant="caption" color="textSecondary" component="div">
                                  {association.trigger?.requestType || 'No event name set'}
                                </WalteroTypography>
                                
                                <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', mt: 1 }}>
                                  <strong>Action:</strong> {association.action?.actionName || 'Unknown Action'}
                                </WalteroTypography>
                                <WalteroTypography variant="caption" color="textSecondary" component="div">
                                  {association.action?.actionDescription || 'No description'}
                                </WalteroTypography>
                              </Box>
                              
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleDeleteTriggerAction(`${association.triggerId}-${association.actionId}`)}
                              >
                                Remove
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setCreateDialogOpen(false);
              setEditingApplication(null);
              setViewingApplicationDetail(null);
              resetApplicationForm();
            }}
          >
            Cancel
          </Button>
          {!viewingApplicationDetail && (
            <WalteroButton 
              variant="contained"
              onClick={editingApplication ? handleUpdateApplication : handleCreateApplication}
              disabled={!applicationForm.applicationName.trim()}
            >
              {editingApplication ? 'UPDATE APPLICATION' : 'CREATE APPLICATION'}
            </WalteroButton>
          )}
          {viewingApplicationDetail && editingApplication && (
            <WalteroButton 
              variant="contained"
              onClick={handleUpdateApplication}
              disabled={!applicationForm.applicationName.trim()}
            >
              SAVE CHANGES
            </WalteroButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Grant Access Dialog */}
      <Dialog
        open={grantAccessDialogOpen}
        onClose={() => setGrantAccessDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            <span>Grant Application Access</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
              Grant Application Access
            </WalteroTypography>
            <WalteroTypography variant="caption">
              Search and select applications to grant access to the selected organization. You can grant normal access or set an application as the organization's default.
            </WalteroTypography>
          </Alert>
          
          <TextField
            fullWidth
            label="Search Applications"
            placeholder="Type to search applications by name, category, or description..."
            value={applicationSearchTerm}
            onChange={(e) => {
              setApplicationSearchTerm(e.target.value);
              searchApplications(e.target.value);
            }}
            sx={{ mb: 2 }}
          />
          
          {applicationSearchTerm.length >= 2 && (
            <Grid container spacing={2}>
              {searchedApplications.map(app => (
                <Grid item xs={12} key={app.applicationId}>
                  <Card>
                    <CardContent>
                      <WalteroTypography variant="h6">
                        {app.applicationName}
                      </WalteroTypography>
                      <WalteroTypography variant="body2" color="textSecondary">
                        {app.applicationCategory} • {app.description || 'No description'}
                      </WalteroTypography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                      <WalteroTypography variant="caption" color="textSecondary">
                        Version: {app.version || '1.0.0'}
                      </WalteroTypography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <WalteroButton 
                          size="small"
                          variant="outlined"
                          onClick={() => handleGrantAccess(app.applicationId, false)}
                        >
                          GRANT ACCESS
                        </WalteroButton>
                        <WalteroButton 
                          size="small"
                          variant="contained"
                          onClick={() => handleGrantAccess(app.applicationId, true)}
                        >
                          SET AS DEFAULT
                        </WalteroButton>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          
          {applicationSearchTerm.length >= 2 && searchedApplications.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <WalteroTypography variant="body2" color="textSecondary">
                No applications found matching "{applicationSearchTerm}"
              </WalteroTypography>
            </Box>
          )}
          
          {applicationSearchTerm.length < 2 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <WalteroTypography variant="body2" color="textSecondary">
                Type at least 2 characters to search for applications
              </WalteroTypography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => {
            setGrantAccessDialogOpen(false);
            setApplicationSearchTerm('');
            setSearchedApplications([]);
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Connection Dialog */}
      <Dialog
        open={editConnectionDialogOpen}
        onClose={() => {
          setEditConnectionDialogOpen(false);
          setEditingConnection(null);
          setSensorSearchTerm('');
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            <span>Edit Application Access & Sensor Assignments</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingConnection && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Left side - Application details */}
              <Grid item xs={12} md={5}>
                <WalteroCard>
                  <WalteroTypography variant="h6" gutterBottom>
                    Application Settings
                  </WalteroTypography>
                  
                  <WalteroTypography variant="body1" gutterBottom>
                    <strong>Application:</strong> {editingConnection.applicationName}
                  </WalteroTypography>
                  <WalteroTypography variant="body2" color="textSecondary" gutterBottom>
                    Category: {editingConnection.applicationCategory}
                  </WalteroTypography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingConnection.isDefault || false}
                        onChange={(e) => setEditingConnection(prev => ({
                          ...prev,
                          isDefault: e.target.checked
                        }))}
                      />
                    }
                    label="Default application for this organization"
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Notes"
                    value={editingConnection.notes || ''}
                    onChange={(e) => setEditingConnection(prev => ({
                      ...prev,
                      notes: e.target.value
                    }))}
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                  
                  <WalteroTypography variant="caption" color="textSecondary">
                    Granted on: {editingConnection.grantedDate ? new Date(editingConnection.grantedDate).toLocaleDateString() : 'N/A'}
                  </WalteroTypography>
                </WalteroCard>
              </Grid>
              
              {/* Right side - Sensor assignments */}
              <Grid item xs={12} md={7}>
                <WalteroCard>
                  <Box sx={{ mb: 2 }}>
                    <WalteroTypography variant="h6" gutterBottom>
                      Sensor Assignments
                    </WalteroTypography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <WalteroTypography variant="caption">
                        <strong>Explicit assignments:</strong> Chips with a "×" icon represent explicit sensor-to-application assignments (sensors can have more than one).<br/>
                        <strong>Effective assignment:</strong> The filled chip highlights the application currently in effect. Sensors without explicit chips fall back to the organization's default.
                      </WalteroTypography>
                    </Alert>
                    
                    <WalteroTypography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {getFilteredSensors().length} sensors in organization
                    </WalteroTypography>
                  </Box>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Search sensors"
                    placeholder="Search by name, serial number, or meter ID..."
                    value={sensorSearchTerm}
                    onChange={(e) => setSensorSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    }}
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
                    {sensorsLoading ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography>Loading sensors...</Typography>
                      </Box>
                    ) : (
                      <List>
                        {getFilteredSensors().map((sensor) => {
                          const applicationChips = getSensorApplicationChips(sensor);
                          
                          return (
                            <ListItem key={sensor.id} sx={{ 
                              border: '1px solid', 
                              borderColor: 'divider', 
                              borderRadius: 1, 
                              mb: 1,
                              backgroundColor: 'background.paper'
                            }}>
                              <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box>
                                    <WalteroTypography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {sensor.name || sensor.serialnumber || 'Unnamed Sensor'}
                                    </WalteroTypography>
                                    <WalteroTypography variant="caption" color="textSecondary">
                                      Serial: {sensor.serialnumber} | Meter: {sensor.externalmeterid || 'N/A'}
                                    </WalteroTypography>
                                  </Box>
                                  
                                  <WalteroButton 
                                    size="small" 
                                    variant="outlined"
                                    onClick={() => handleAssignSensorToApplication(sensor.id, editingConnection.applicationId)}
                                    sx={{ ml: 2 }}
                                    title={`Assign sensor to ${editingConnection.applicationName}`}
                                  >
                                    ASSIGN TO {editingConnection.applicationName.toUpperCase()}
                                  </WalteroButton>
                                </Box>
                                
                                <Box sx={{ mt: 1 }}>
                                  {applicationChips.length > 0 ? (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                      {applicationChips.map((chip) => {
                                        const chipColor = chip.isDefault ? 'secondary' : chip.isEffective ? 'primary' : 'default';
                                        const chipVariant = chip.isDefault ? 'outlined' : chip.isEffective ? 'filled' : 'outlined';
                                        const chipTitleParts = [];
                                        if (chip.isExplicit) {
                                          chipTitleParts.push('Explicit assignment');
                                        }
                                        if (chip.isDefault) {
                                          chipTitleParts.push('Organization default');
                                        }
                                        if (chip.isEffective) {
                                          chipTitleParts.push('Effective application');
                                        }
                                        if (chip.assignedDate) {
                                          chipTitleParts.push(`Assigned ${new Date(chip.assignedDate).toLocaleString()}`);
                                        }

                                        return (
                                          <Chip
                                            key={`${sensor.id}-${chip.applicationId}`}
                                            label={chip.applicationName}
                                            size="small"
                                            color={chipColor}
                                            variant={chipVariant}
                                            onDelete={chip.isExplicit ? () => handleRemoveSensorAssignment(sensor.id, chip.applicationId) : undefined}
                                            deleteIcon={chip.isExplicit ? <CloseIcon /> : undefined}
                                            title={chipTitleParts.join(' • ') || chip.applicationName}
                                            sx={{
                                              opacity: chip.isEffective ? 1 : 0.8,
                                              '& .MuiChip-deleteIcon': {
                                                fontSize: '16px'
                                              }
                                            }}
                                          />
                                        );
                                      })}
                                      {applicationChips[0]?.isDefault && (
                                        <Chip
                                          label="organization default"
                                          size="small"
                                          variant="outlined"
                                          sx={{ 
                                            color: 'text.secondary', 
                                            borderColor: 'text.disabled',
                                            fontSize: '0.7rem'
                                          }}
                                        />
                                      )}
                                    </Stack>
                                  ) : (
                                    <WalteroTypography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                                      No application assigned (no organization default set)
                                    </WalteroTypography>
                                  )}
                                </Box>
                              </Box>
                            </ListItem>
                          );
                        })}
                        {getFilteredSensors().length === 0 && (
                          <ListItem>
                            <ListItemText
                              primary="No sensors found"
                              secondary={sensorSearchTerm ? `No sensors match "${sensorSearchTerm}"` : "No sensors in this organization"}
                            />
                          </ListItem>
                        )}
                      </List>
                    )}
                  </Box>
                </WalteroCard>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'space-between' }}>
          <Box>
            <Button 
              onClick={() => {
                setEditConnectionDialogOpen(false);
                setEditingConnection(null);
                setSensorSearchTerm('');
              }}
            >
              Cancel
            </Button>
            <WalteroButton 
              variant="outlined"
              color="error"
              onClick={() => handleRevokeAccess(editingConnection.applicationId)}
              disabled={editingConnection?.isDefault}
              title={editingConnection?.isDefault ? "Cannot revoke default application - set another application as default first" : "Revoke organization access to this application"}
              sx={{ ml: 1 }}
            >
              REVOKE ACCESS
            </WalteroButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <WalteroButton 
              variant="contained"
              onClick={() => handleUpdateConnection(
                editingConnection.isDefault,
                editingConnection.notes
              )}
            >
              SAVE SETTINGS
            </WalteroButton>
          </Box>
        </DialogActions>
      </Dialog>
      
      {/* Assignment Search Dialog */}
      <Dialog
        open={assignmentSearchDialogOpen}
        onClose={() => {
          setAssignmentSearchDialogOpen(false);
          setSelectedSensorForAssignment(null);
          setAvailableApplications([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            <span>Assign Application to Sensor</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSensorForAssignment && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                  Assigning Application to: {selectedSensorForAssignment.name || selectedSensorForAssignment.serialnumber || 'Unnamed Sensor'}
                </WalteroTypography>
                <WalteroTypography variant="caption">
                  Serial: {selectedSensorForAssignment.serialnumber} | Meter: {selectedSensorForAssignment.externalmeterid || 'N/A'}
                </WalteroTypography>
              </Alert>
              
              <WalteroTypography variant="h6" gutterBottom>
                Available Applications
              </WalteroTypography>
              
              {availableApplications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <WalteroTypography variant="body2" color="textSecondary">
                    No applications available for assignment. Grant applications to this organization first.
                  </WalteroTypography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {availableApplications.map((app) => {
                    const currentAppChips = getSensorApplicationChips(selectedSensorForAssignment);
                    const hasExplicitAssignment = currentAppChips.some(chip => 
                      chip.applicationId === app.applicationId && chip.isExplicit
                    );
                    const usesDefaultAssignment = !hasExplicitAssignment && currentAppChips.some(chip => 
                      chip.applicationId === app.applicationId && chip.isDefault
                    );
                    const borderColor = hasExplicitAssignment ? 'primary.main' : usesDefaultAssignment ? 'secondary.main' : 'divider';
                    const borderWidth = hasExplicitAssignment || usesDefaultAssignment ? '2px solid' : '1px solid';
                    
                    return (
                      <Grid item xs={12} key={app.applicationId}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            },
                            border: borderWidth,
                            borderColor
                          }}
                          onClick={() => {
                            handleAssignSensorToApplication(selectedSensorForAssignment.id, app.applicationId);
                            setAssignmentSearchDialogOpen(false);
                            setSelectedSensorForAssignment(null);
                            setAvailableApplications([]);
                          }}
                        >
                          <CardContent sx={{ pb: '16px !important' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <WalteroTypography variant="body1" sx={{ fontWeight: 'medium' }}>
                                  {app.applicationName}
                                  {app.isDefault && (
                                    <Chip 
                                      label="org default" 
                                      size="small" 
                                      color="success" 
                                      variant="outlined"
                                      sx={{ ml: 1, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </WalteroTypography>
                                <WalteroTypography variant="body2" color="textSecondary">
                                  {app.applicationCategory} • {app.description || 'No description'}
                                </WalteroTypography>
                              </Box>
                              
                              {hasExplicitAssignment && (
                                <Chip 
                                  label="Explicitly Assigned" 
                                  size="small" 
                                  color="primary" 
                                  variant="filled"
                                  icon={<CheckCircleIcon />}
                                />
                              )}
                              {!hasExplicitAssignment && usesDefaultAssignment && (
                                <Chip 
                                  label="Uses Default" 
                                  size="small" 
                                  color="secondary" 
                                  variant="outlined"
                                  icon={<RadioButtonUncheckedIcon />}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setAssignmentSearchDialogOpen(false);
              setSelectedSensorForAssignment(null);
              setAvailableApplications([]);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Assignment Dialog */}
      <Dialog
        open={bulkAssignDialogOpen}
        onClose={() => {
          setBulkAssignDialogOpen(false);
          setSelectedApplication('');
          setBulkSensorIds('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AppsIcon color="primary" />
            Bulk Sensor Assignment
          </Box>
        </DialogTitle>
        <DialogContent>
          <WalteroTypography variant="body2" sx={{ mb: 3 }}>
            Assign multiple sensors to an application by entering their IDs below. You can enter IDs separated by commas or new lines.
          </WalteroTypography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Application</InputLabel>
            <Select
              value={selectedApplication}
              onChange={(e) => setSelectedApplication(e.target.value)}
              label="Select Application"
            >
              {applications.filter(app => app.status === 'active').map(app => (
                <MenuItem key={app.applicationId} value={app.applicationId}>
                  {app.applicationName} ({app.applicationCategory})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Sensor IDs"
            placeholder="Enter sensor IDs separated by commas or new lines..."
            value={bulkSensorIds}
            onChange={(e) => setBulkSensorIds(e.target.value)}
            multiline
            rows={6}
            sx={{ mb: 2 }}
            helperText="Example: sensor-id-1, sensor-id-2, sensor-id-3"
          />
          
          <Alert severity="info" sx={{ mb: 2 }}>
            You can also unassign sensors to revert them to their organization's default application.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBulkAssignDialogOpen(false);
            setSelectedApplication('');
            setBulkSensorIds('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkUnassignSensors}
            color="error"
            disabled={!bulkSensorIds.trim()}
          >
            UNASSIGN ({bulkSensorIds.split(/[,\n]/).filter(id => id.trim()).length})
          </Button>
          <WalteroButton 
            variant="contained"
            onClick={handleBulkAssignSensors}
            disabled={!selectedApplication || !bulkSensorIds.trim()}
          >
            ASSIGN ({bulkSensorIds.split(/[,\n]/).filter(id => id.trim()).length})
          </WalteroButton>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Trigger Dialog */}
      <Dialog
        open={triggerDialogOpen}
        onClose={() => {
          setTriggerDialogOpen(false);
          setEditingTrigger(null);
          resetTriggerForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingTrigger ? <EditIcon /> : <AddIcon />}
            <span>{editingTrigger ? 'Edit Trigger' : 'Create New Trigger'}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Trigger Name"
              value={triggerForm.triggerName}
              onChange={(e) => setTriggerForm(prev => ({
                ...prev,
                triggerName: e.target.value
              }))}
              required
              fullWidth
              helperText="Descriptive name for this trigger"
            />
            
            <TextField
              label="Description"
              value={triggerForm.triggerDescription}
              onChange={(e) => setTriggerForm(prev => ({
                ...prev,
                triggerDescription: e.target.value
              }))}
              multiline
              rows={2}
              fullWidth
              helperText="Optional description of what this trigger monitors"
            />

            <TextField
              label="Event Name"
              value={triggerForm.requestType}
              onChange={(e) => setTriggerForm(prev => ({
                ...prev,
                requestType: e.target.value
              }))}
              required
              fullWidth
              helperText="Event request type to match (e.g., 'v1/sensor/image/event/received')"
              placeholder="v1/sensor/image/event/received"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => {
            setTriggerDialogOpen(false);
            setEditingTrigger(null);
            resetTriggerForm();
          }}>
            Cancel
          </Button>
          <WalteroButton
            variant="contained"
            onClick={editingTrigger ? handleUpdateTrigger : handleCreateTrigger}
            disabled={!triggerForm.triggerName.trim() || !triggerForm.requestType.trim()}
          >
            {editingTrigger ? 'UPDATE TRIGGER' : 'CREATE TRIGGER'}
          </WalteroButton>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => {
          setActionDialogOpen(false);
          setEditingAction(null);
          resetActionForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingAction ? <EditIcon /> : <AddIcon />}
            <span>{editingAction ? 'Edit Action' : 'Create New Action'}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Action Name"
              value={actionForm.actionName}
              onChange={(e) => setActionForm(prev => ({
                ...prev,
                actionName: e.target.value
              }))}
              required
              fullWidth
              helperText="Descriptive name for this action"
            />
            
            <TextField
              label="Description"
              value={actionForm.actionDescription}
              onChange={(e) => setActionForm(prev => ({
                ...prev,
                actionDescription: e.target.value
              }))}
              multiline
              rows={2}
              fullWidth
              helperText="Optional description of what this action does"
            />

            <TextField
              label="Step Function ARN"
              value={actionForm.stepFunctionArn}
              onChange={(e) => setActionForm(prev => ({
                ...prev,
                stepFunctionArn: e.target.value
              }))}
              required
              fullWidth
              helperText="AWS Step Function ARN that will be executed"
              placeholder="arn:aws:states:region:account:stateMachine:MyStateMachine"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => {
            setActionDialogOpen(false);
            setEditingAction(null);
            resetActionForm();
          }}>
            Cancel
          </Button>
          <WalteroButton
            variant="contained"
            onClick={editingAction ? handleUpdateAction : handleCreateAction}
            disabled={!actionForm.actionName.trim() || !actionForm.stepFunctionArn.trim()}
          >
            {editingAction ? 'UPDATE ACTION' : 'CREATE ACTION'}
          </WalteroButton>
        </DialogActions>
      </Dialog>

      {/* Create Association Dialog */}
      <Dialog
        open={associationDialogOpen}
        onClose={() => {
          setAssociationDialogOpen(false);
          resetAssociationForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            <span>Create Trigger-Action Association</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <WalteroTypography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
              Associate Trigger with Action
            </WalteroTypography>
            <WalteroTypography variant="caption">
              Link a trigger event to an action that will be executed when the trigger fires.
              Multiple associations can be created with different priorities.
            </WalteroTypography>
          </Alert>

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Application</InputLabel>
              <Select
                value={selectedApplicationForAssociation}
                onChange={(e) => setSelectedApplicationForAssociation(e.target.value)}
                label="Application"
              >
                {applications.filter(app => app.status === 'active').map(app => (
                  <MenuItem key={app.applicationId} value={app.applicationId}>
                    {app.applicationName} ({app.applicationCategory})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Trigger</InputLabel>
                  <Select
                    value={associationForm.triggerId}
                    onChange={(e) => setAssociationForm(prev => ({
                      ...prev,
                      triggerId: e.target.value
                    }))}
                    label="Trigger"
                    sx={{ minWidth: 200 }}
                    disabled={triggersLoading}
                  >
                    {triggersLoading ? (
                      <MenuItem disabled>
                        <Typography>Loading triggers...</Typography>
                      </MenuItem>
                    ) : (() => {
                      console.log('Rendering triggers dropdown, availableTriggers:', availableTriggers);
                      const activeTriggers = availableTriggers.filter(t => t.isActive === true);
                      console.log('Active triggers:', activeTriggers);
                      return activeTriggers.map(trigger => (
                      <MenuItem key={trigger.triggerId} value={trigger.triggerId}>
                        <Box>
                          <Typography variant="body2">{trigger.triggerName}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {trigger.requestType || 'No event name'}
                          </Typography>
                        </Box>
                      </MenuItem>
                      ));
                    })()}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={associationForm.actionId}
                    onChange={(e) => setAssociationForm(prev => ({
                      ...prev,
                      actionId: e.target.value
                    }))}
                    label="Action"
                    sx={{ minWidth: 200 }}
                    disabled={actionsLoading}
                  >
                    {actionsLoading ? (
                      <MenuItem disabled>
                        <Typography>Loading actions...</Typography>
                      </MenuItem>
                    ) : (() => {
                      console.log('Rendering actions dropdown, availableActions:', availableActions);
                      const activeActions = availableActions.filter(a => a.isActive === true);
                      console.log('Active actions:', activeActions);
                      return activeActions.map(action => (
                      <MenuItem key={action.actionId} value={action.actionId}>
                        <Box>
                          <Typography variant="body2">{action.actionName}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {action.actionDescription || 'No description'}
                          </Typography>
                        </Box>
                      </MenuItem>
                      ));
                    })()}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  type="number"
                  label="Priority"
                  value={associationForm.priority}
                  onChange={(e) => setAssociationForm(prev => ({
                    ...prev,
                    priority: parseInt(e.target.value) || 1
                  }))}
                  required
                  fullWidth
                  inputProps={{ min: 1, max: 100 }}
                  helperText="Lower numbers = higher priority"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={associationForm.isActive}
                      onChange={(e) => setAssociationForm(prev => ({
                        ...prev,
                        isActive: e.target.checked
                      }))}
                    />
                  }
                  label="Active"
                  sx={{ mt: 2 }}
                />
              </Grid>
            </Grid>
            
            <TextField
              label="Configuration (JSON)"
              value={associationForm.configuration}
              onChange={(e) => setAssociationForm(prev => ({
                ...prev,
                configuration: e.target.value
              }))}
              multiline
              rows={3}
              fullWidth
              helperText="Optional JSON configuration for custom parameters"
              placeholder='{"threshold": 10, "notify": true}'
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => {
            setAssociationDialogOpen(false);
            resetAssociationForm();
          }}>
            Cancel
          </Button>
          <WalteroButton
            variant="contained"
            onClick={handleCreateAssociation}
            disabled={
              !selectedApplicationForAssociation ||
              !associationForm.triggerId ||
              !associationForm.actionId
            }
          >
            CREATE ASSOCIATION
          </WalteroButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplicationsTab;
